/**
 * RoCheckbox - 方框多选组 Web 组件（Figma 5490:16480 / 5485:15113）
 * 项间距 16px；框与文案 gap 10px；detail.values 恒为 string[]
 * - size：与 RoSelect 一致 large | medium | small（默认 large：框 20 / 16 / 14px）
 * - checkbox-width / checkbox-height：覆盖方框尺寸（px，可只写一侧）
 * - options JSON 支持 label_html（无子 <option> 时）；展示用 innerHTML，提交值见 default-option-value 回落
 * - 提交值：<option> 链为 非空 value 属性 → label 文本 → default-option-value（默认 "1"）；JSON 为 非空 value → default-option-value；富文本不用 label 参与提交键
 * - unchecked-value：仅单字段模式且无选中时 setFormValue 为该值（如 "0"）
 * - 行为 row 为 div[role=checkbox]，以便标签内可含 <a>
 * - 必填文案：error-message 或 window.RoFormLocale.required（见 snippets/ro-form-locale.liquid）
 */
class RoCheckbox extends HTMLElement {
  static formAssociated = true;

  static _normalizeColumns(raw) {
    if (raw == null || String(raw).trim() === "") return null;
    const n = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n) || n < 1) return null;
    return Math.min(n, 24);
  }

  /**
   * 与 RoSelect 一致：large | medium | small；空或非法 → large
   */
  static _normalizeSize(raw) {
    if (raw == null || String(raw).trim() === "") return "large";
    const s = String(raw).trim().toLowerCase();
    if (s === "medium" || s === "small" || s === "large") return s;
    return "large";
  }

  static _parseCssLengthPx(raw) {
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!s) return null;
    const n = parseFloat(s.replace(/px$/i, "").trim());
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  }

  _defaultOptionValue() {
    const a = this.getAttribute("default-option-value");
    if (a != null && String(a).trim() !== "") return String(a).trim();
    return "1";
  }

  /**
   * <option>：非空 value 属性 → textContent → default
   */
  static _optionResolvedSubmitValue(o, defaultVal) {
    if (o.hasAttribute("value")) {
      const v = o.getAttribute("value");
      if (v !== "") return v;
    }
    const label = (o.textContent || "").trim();
    if (label !== "") return label;
    return defaultVal;
  }

  static get observedAttributes() {
    return [
      "value",
      "disabled",
      "required",
      "options",
      "default-value",
      "error-message",
      "validate-on-change",
      "columns",
      "size",
      "checkbox-width",
      "checkbox-height",
      "default-option-value",
      "unchecked-value",
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._internals = this.attachInternals();
    this._options = [];
    this._checkedSet = new Set();
    this._isError = false;
    this._errorMessage = "";
    this._boundRowKeydown = this._onRowKeydown.bind(this);
    this._mo = null;
    this._render();
  }

  _render() {
    const style = document.createElement("style");
    style.textContent = [
      ":host{display:inline-flex;flex-direction:column;width:100%;vertical-align:top}",
      ":host{--ro-checkbox-w:20px;--ro-checkbox-h:20px;--ro-checkbox-border-w:1.25px;--ro-checkbox-check-scale:1}",
      ":host([size=medium]){--ro-checkbox-w:16px;--ro-checkbox-h:16px;--ro-checkbox-border-w:1px;--ro-checkbox-check-scale:0.833}",
      ":host([size=small]){--ro-checkbox-w:14px;--ro-checkbox-h:14px;--ro-checkbox-border-w:1px;--ro-checkbox-check-scale:0.7}",
      ".list{display:flex;flex-direction:column;gap:16px;width:100%}",
      ".list[data-columns]{display:grid}",
      ".row{display:flex;align-items:flex-start;gap:10px;margin:0;padding:0;cursor:pointer;font:inherit;color:inherit;text-align:left;width:100%;background:none;border:none;box-sizing:border-box}",
      ":host(:dir(rtl)) .row{text-align:right}",
      ".row:focus-visible{outline:2px solid var(--brand-color,#ea0029);outline-offset:2px;border-radius:4px}",
      ".row[aria-disabled=true]{cursor:not-allowed;opacity:0.55}",
      ".checkbox-visual{width:var(--ro-checkbox-w);height:var(--ro-checkbox-h);flex-shrink:0;box-sizing:border-box;border:var(--ro-checkbox-border-w) solid var(--main-color,#161616);border-radius:4px;background:var(--bg-white-color,#fff);display:flex;align-items:center;justify-content:center;}",
      ".checkbox-visual.checked{background:var(--brand-color,#ea0029);border-color:var(--brand-color,#ea0029)}",
      ".check-svg{width:calc(12px * var(--ro-checkbox-check-scale));height:calc(10px * var(--ro-checkbox-check-scale));display:block;flex-shrink:0}",
      ".checkbox-visual:not(.checked) .check-svg{opacity:0}",
      ".label-text{flex:1 1 0;min-width:0;font-weight:400;font-size:16px;line-height:20px;color:var(--ro-checkbox-text-color,var(--main-color,#161616))}",
      ".label-text :is(p,ul,ol,li,h1,h2,h3,h4,h5,h6,blockquote,pre,figure){margin:0;padding:0}",
      ".label-text a{color:var(--ro-checkbox-link-color,inherit);text-decoration:underline}",
      ".label-text--rich{color:var(--ro-checkbox-text-color,#535353)}",
      ".label-text--rich a{color:var(--ro-checkbox-link-color,#000000)}",
      ":host([size=medium]) .label-text{font-size:16px;line-height:16px}",
      ":host([size=small]) .label-text{font-size:14px;line-height:14px}",
      ":host([disabled]) .row{cursor:not-allowed}",
      ".error-message{display:none;margin-top:8px;padding:0 16px;color:var(--error-color,#d93840);font-weight:400;font-size:var(--error-font-size,12px);line-height:var(--error-line-height,16px)}",
      ":host([error]) .error-message{display:block}",
    ].join("");
    const list = document.createElement("div");
    list.className = "list";
    list.setAttribute("part", "list");
    list.setAttribute("role", "group");
    const err = document.createElement("div");
    err.className = "error-message";
    err.setAttribute("part", "error");
    err.setAttribute("aria-live", "polite");
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(list);
    this.shadowRoot.appendChild(err);
    this._listEl = list;
    this._errorEl = err;
  }

  /** 仅在显式写了非法 size 时写回规范值；未写 size 时不添加属性（默认 large 由基础 :host 样式体现） */
  _syncSizeAttribute() {
    const raw = this.getAttribute("size");
    if (raw == null || String(raw).trim() === "") return;
    const n = RoCheckbox._normalizeSize(raw);
    if (String(raw).trim().toLowerCase() !== n) this.setAttribute("size", n);
  }

  /** checkbox-width/height 覆盖 CSS 预设；无属性时删掉宿主上的变量，让 :host([size]) 生效 */
  _syncBoxMetrics() {
    const w = RoCheckbox._parseCssLengthPx(this.getAttribute("checkbox-width"));
    const h = RoCheckbox._parseCssLengthPx(this.getAttribute("checkbox-height"));
    const size = RoCheckbox._normalizeSize(this.getAttribute("size"));
    const preset = { large: 20, medium: 16, small: 14 }[size] || 20;
    if (w != null) this.style.setProperty("--ro-checkbox-w", w + "px");
    else this.style.removeProperty("--ro-checkbox-w");
    if (h != null) this.style.setProperty("--ro-checkbox-h", h + "px");
    else this.style.removeProperty("--ro-checkbox-h");
    const effW = w != null ? w : preset;
    const effH = h != null ? h : preset;
    const edge = Math.min(effW, effH);
    const borderW = edge >= 18 ? 1.25 : 1;
    const scale = edge / 20;
    this.style.setProperty("--ro-checkbox-border-w", borderW + "px");
    this.style.setProperty("--ro-checkbox-check-scale", String(Math.max(0.55, Math.min(1, scale))));
  }

  _isMultiFieldMode() {
    if (this._options.length > 1) return true;
    return /\[\]\s*$/.test((this.getAttribute("name") || "").trim());
  }

  _syncColumnsLayout() {
    if (!this._listEl) return;
    const n = RoCheckbox._normalizeColumns(this.getAttribute("columns"));
    if (n == null) {
      this._listEl.style.display = "";
      this._listEl.style.gridTemplateColumns = "";
      this._listEl.removeAttribute("data-columns");
      return;
    }
    this._listEl.style.display = "grid";
    this._listEl.style.gridTemplateColumns = "repeat(" + n + ", minmax(0, 1fr))";
    this._listEl.setAttribute("data-columns", String(n));
  }

  _ensureOptionsHiddenStyle() {
    if (this.querySelector("style[data-ro-checkbox-options]")) return;
    const s = document.createElement("style");
    s.setAttribute("data-ro-checkbox-options", "");
    s.textContent = ":host > option { display: none !important; }";
    this.insertBefore(s, this.firstChild);
  }

  /**
   * 解析 options 属性 JSON；失败时再尝试 HTML 实体解码（如整段被 escape 成 &quot;）
   */
  static _parseOptionsAttrJson(raw) {
    if (raw == null || String(raw).trim() === "") return null;
    const s = String(raw);
    try {
      const arr = JSON.parse(s);
      return Array.isArray(arr) ? arr : null;
    } catch (_) {
      /* fallthrough */
    }
    try {
      const ta = document.createElement("textarea");
      ta.innerHTML = s;
      const arr = JSON.parse(ta.value);
      return Array.isArray(arr) ? arr : null;
    } catch (_) {
      return null;
    }
  }

  _buildOptions() {
    const def = this._defaultOptionValue();
    const fromChildren = this.querySelectorAll("option");
    if (fromChildren.length) {
      this._options = Array.from(fromChildren).map((o) => {
        const value = RoCheckbox._optionResolvedSubmitValue(o, def);
        const plainLabel = (o.textContent || "").trim();
        const label = plainLabel || value || "";
        return {
          value,
          label,
          labelHtml: "",
          labelIsHtml: false,
          disabled: o.disabled || o.hasAttribute("disabled"),
        };
      });
      return;
    }
    const optsAttr = this.getAttribute("options");
    if (optsAttr) {
      const arr = RoCheckbox._parseOptionsAttrJson(optsAttr);
      this._options =
        arr != null
          ? arr.map((o) => {
              const rawV = o.value ?? o.id;
              const hasVal =
                rawV != null && String(rawV).trim() !== "";
              const value = hasVal ? String(rawV).trim() : def;
              const labelHtml =
                o.label_html != null && String(o.label_html) !== ""
                  ? String(o.label_html)
                  : "";
              const plain =
                o.label != null && String(o.label) !== ""
                  ? String(o.label)
                  : o.name != null && String(o.name) !== ""
                    ? String(o.name)
                    : o.text != null && String(o.text) !== ""
                      ? String(o.text)
                      : "";
              const labelIsHtml = labelHtml !== "";
              const label = labelIsHtml
                ? plain || value
                : plain || value || "";
              return {
                value,
                label,
                labelHtml: labelIsHtml ? labelHtml : "",
                labelIsHtml,
                disabled: !!o.disabled,
              };
            })
          : [];
      return;
    }
    this._options = [];
  }

  _orderedCheckedValues() {
    const out = [];
    for (const o of this._options) {
      if (this._checkedSet.has(o.value)) out.push(o.value);
    }
    return out;
  }

  _uncheckedSentinel() {
    const u = this.getAttribute("unchecked-value");
    if (u == null) return null;
    return String(u);
  }

  /** 对外序列化：多字段为 JSON.stringify(values)；单字段为标量；无选中时单字段可有 unchecked-value */
  _scalarDetailValue(values) {
    if (!values.length) {
      if (!this._isMultiFieldMode()) {
        const u = this._uncheckedSentinel();
        if (u !== null) return u;
      }
      return "";
    }
    if (this._isMultiFieldMode()) return JSON.stringify(values);
    return values[0];
  }

  /** 从 value / default-value 属性解析出待勾选的 value 列表 */
  _parseValueAttributeToCheckedKeys(raw) {
    const t = String(raw).trim();
    if (!t) return [];
    if (this._isMultiFieldMode()) {
      try {
        const arr = JSON.parse(t);
        return Array.isArray(arr) ? arr.map((x) => String(x)) : [];
      } catch (_) {
        return [];
      }
    }
    const u = this._uncheckedSentinel();
    if (u !== null && t === u) return [];
    return [t];
  }

  _hydrateCheckedFromAttributes() {
    this._checkedSet.clear();
    let raw = this.getAttribute("value");
    if (raw == null || raw === "") raw = this.getAttribute("default-value");
    if (raw == null || raw === "") return;
    this._parseValueAttributeToCheckedKeys(raw).forEach((v) => {
      if (this._options.some((o) => o.value === v)) this._checkedSet.add(v);
    });
  }

  _syncValueAttribute() {
    const values = this._orderedCheckedValues();
    if (!values.length) {
      this.removeAttribute("value");
      return;
    }
    if (this._isMultiFieldMode()) this.setAttribute("value", JSON.stringify(values));
    else this.setAttribute("value", values[0]);
  }

  _syncFormValue() {
    const name = this.getAttribute("name");
    if (!name) {
      this._internals.setFormValue(null);
      return;
    }
    const values = this._orderedCheckedValues();
    if (values.length === 0) {
      if (!this._isMultiFieldMode()) {
        const u = this._uncheckedSentinel();
        if (u !== null) {
          this._internals.setFormValue(u);
          return;
        }
      }
      this._internals.setFormValue(null);
      return;
    }
    if (this._isMultiFieldMode()) {
      const fd = new FormData();
      for (const v of values) fd.append(name, v);
      this._internals.setFormValue(fd);
      return;
    }
    this._internals.setFormValue(values[0]);
  }

  _renderRows() {
    this._listEl.innerHTML = "";
    const svgNS = "http://www.w3.org/2000/svg";
    const labelledby = this.getAttribute("aria-labelledby");
    const hostDisabled = this.hasAttribute("disabled");

    this._options.forEach((opt) => {
      const row = document.createElement("div");
      row.className = "row";
      row.part = "row";
      row.dataset.value = opt.value;
      row.setAttribute("role", "checkbox");
      const checked = this._checkedSet.has(opt.value);
      row.setAttribute("aria-checked", checked ? "true" : "false");
      if (labelledby) row.setAttribute("aria-labelledby", labelledby);

      const vis = document.createElement("span");
      vis.className = "checkbox-visual" + (checked ? " checked" : "");
      vis.setAttribute("aria-hidden", "true");
      const svg = document.createElementNS(svgNS, "svg");
      svg.setAttribute("class", "check-svg");
      svg.setAttribute("viewBox", "0 0 12 10");
      svg.setAttribute("fill", "none");
      const path = document.createElementNS(svgNS, "path");
      path.setAttribute("d", "M1 5.2L4.2 8.4L11 1.5");
      path.setAttribute("stroke", "#fff");
      path.setAttribute("stroke-width", "1.8");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");
      svg.appendChild(path);
      vis.appendChild(svg);

      const label = document.createElement("span");
      label.className =
        opt.labelIsHtml && opt.labelHtml ? "label-text label-text--rich" : "label-text";
      if (opt.labelIsHtml && opt.labelHtml) label.innerHTML = opt.labelHtml;
      else label.textContent = opt.label;

      row.appendChild(vis);
      row.appendChild(label);

      const optDisabled = opt.disabled || hostDisabled;
      row.setAttribute("aria-disabled", optDisabled ? "true" : "false");
      if (optDisabled) row.setAttribute("tabindex", "-1");
      if (!optDisabled) {
        row.addEventListener("click", (e) => {
          if (e.target instanceof Element && e.target.closest("a")) return;
          this._toggle(opt.value, false);
        });
        row.addEventListener("keydown", this._boundRowKeydown);
      }

      this._listEl.appendChild(row);
    });

    this._syncRovingTabindex();
  }

  _getEnabledRows() {
    return Array.from(
      this._listEl.querySelectorAll('.row:not([aria-disabled="true"])')
    );
  }

  _syncRovingTabindex() {
    const rows = this._getEnabledRows();
    rows.forEach((r, i) => r.setAttribute("tabindex", i === 0 ? "0" : "-1"));
  }

  _toggle(value, optDisabled) {
    if (this.hasAttribute("disabled") || optDisabled) return;
    if (this._checkedSet.has(value)) this._checkedSet.delete(value);
    else this._checkedSet.add(value);
    this._commitChange();
    if (this.hasAttribute("validate-on-change")) this.validate();
  }

  _commitChange() {
    this._syncValueAttribute();
    this._syncFormValue();
    this._renderRows();
    const values = this._orderedCheckedValues();
    this.dispatchEvent(
      new CustomEvent("change", {
        bubbles: true,
        detail: { values, value: this._scalarDetailValue(values) },
      })
    );
  }

  _onRowKeydown(e) {
    if (this.hasAttribute("disabled")) return;
    const rows = this._getEnabledRows();
    const i = rows.indexOf(e.currentTarget);
    if (i < 0) return;

    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      const v = e.currentTarget.dataset.value;
      const opt = this._options.find((o) => o.value === v);
      this._toggle(v, opt && opt.disabled);
      return;
    }

    let next = i;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      next = Math.min(rows.length - 1, i + 1);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      next = Math.max(0, i - 1);
    } else return;

    if (next !== i) {
      rows[next].focus();
      rows.forEach((r, j) => r.setAttribute("tabindex", j === next ? "0" : "-1"));
    }
  }

  connectedCallback() {
    this._syncSizeAttribute();
    this._syncBoxMetrics();
    this._ensureOptionsHiddenStyle();
    this._buildOptions();
    this._hydrateCheckedFromAttributes();
    this._syncFormValue();
    this._renderRows();
    this._syncColumnsLayout();
    if (!this._mo) {
      this._mo = new MutationObserver(() => {
        this._buildOptions();
        this._hydrateCheckedFromAttributes();
        this._renderRows();
      });
      this._mo.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["value", "disabled"],
      });
    }
  }

  disconnectedCallback() {
    if (this._mo) {
      this._mo.disconnect();
      this._mo = null;
    }
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal) return;
    switch (name) {
      case "value":
        this._checkedSet.clear();
        if (newVal != null && newVal !== "") {
          this._parseValueAttributeToCheckedKeys(newVal).forEach((v) => {
            if (this._options.some((o) => o.value === v)) this._checkedSet.add(v);
          });
        }
        this._syncFormValue();
        this._renderRows();
        break;
      case "options":
        this._buildOptions();
        this._hydrateCheckedFromAttributes();
        this._renderRows();
        break;
      case "disabled":
        this._renderRows();
        break;
      case "columns":
        this._syncColumnsLayout();
        break;
      case "size":
        this._syncSizeAttribute();
        this._syncBoxMetrics();
        break;
      case "checkbox-width":
      case "checkbox-height":
        this._syncBoxMetrics();
        break;
      case "default-option-value":
        this._buildOptions();
        this._hydrateCheckedFromAttributes();
        this._renderRows();
        this._syncFormValue();
        break;
      case "unchecked-value":
        this._syncFormValue();
        this._renderRows();
        break;
      case "error-message":
        if (this._isError) this._errorEl.textContent = newVal || this._errorMessage;
        break;
      default:
        break;
    }
  }

  validate() {
    if (this.hasAttribute("disabled")) return { valid: true, message: "" };
    let errorMessage = "";
    if (this.hasAttribute("required") && this._checkedSet.size === 0) {
      errorMessage =
        this.getAttribute("error-message") ||
        window.RoFormLocale?.required ||
        "This field is required";
    }
    const valid = !errorMessage;
    const values = this._orderedCheckedValues();
    this._isError = !valid;
    this._errorMessage = errorMessage;
    if (this._isError) {
      this.setAttribute("error", "");
      this._errorEl.textContent = this.getAttribute("error-message") || errorMessage;
    } else {
      this.removeAttribute("error");
      this._errorEl.textContent = "";
    }
    this.dispatchEvent(
      new CustomEvent("validate", { bubbles: true, detail: { valid, message: errorMessage, value: values } })
    );
    if (!valid) {
      this.dispatchEvent(new CustomEvent("error", { bubbles: true, detail: { message: errorMessage, value: values } }));
    }
    return { valid, message: errorMessage };
  }

  reset() {
    this._checkedSet.clear();
    const def = this.getAttribute("default-value");
    if (def != null && def !== "") {
      this._parseValueAttributeToCheckedKeys(def).forEach((v) => {
        if (this._options.some((o) => o.value === v)) this._checkedSet.add(v);
      });
    }
    this._syncValueAttribute();
    this._syncFormValue();
    this._setError(false, "");
    this._renderRows();
  }

  clearError() {
    this._setError(false, "");
  }

  focus() {
    const r = this._getEnabledRows()[0];
    if (r) r.focus();
  }

  _setError(isError, message) {
    this._isError = isError;
    this._errorMessage = message;
    if (isError) {
      this.setAttribute("error", "");
      this._errorEl.textContent = this.getAttribute("error-message") || message;
    } else {
      this.removeAttribute("error");
      this._errorEl.textContent = "";
    }
  }

  get value() {
    return this._scalarDetailValue(this._orderedCheckedValues());
  }

  set value(val) {
    this._checkedSet.clear();
    const s = val != null ? String(val) : "";
    if (s) {
      this._parseValueAttributeToCheckedKeys(s).forEach((v) => {
        if (this._options.some((o) => o.value === v)) this._checkedSet.add(v);
      });
    }
    this._syncValueAttribute();
    this._syncFormValue();
    this._renderRows();
  }

  get values() {
    return this._orderedCheckedValues();
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }
  set disabled(val) {
    if (val) this.setAttribute("disabled", "");
    else this.removeAttribute("disabled");
  }

  get required() {
    return this.hasAttribute("required");
  }
  set required(val) {
    if (val) this.setAttribute("required", "");
    else this.removeAttribute("required");
  }

  get columns() {
    return RoCheckbox._normalizeColumns(this.getAttribute("columns"));
  }
  set columns(val) {
    if (val == null || val === "") this.removeAttribute("columns");
    else this.setAttribute("columns", String(val));
  }

  /** 规范后档位：large | medium | small */
  get size() {
    return RoCheckbox._normalizeSize(this.getAttribute("size"));
  }
  set size(val) {
    if (val == null || val === "") this.removeAttribute("size");
    else this.setAttribute("size", String(val));
  }

  formResetCallback() {
    this.reset();
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
    this._renderRows();
  }
}

if (!customElements.get("ro-checkbox")) {
  customElements.define("ro-checkbox", RoCheckbox);
}
